import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BUSINESS_UNITS, BusinessUnit } from '@/constants/businessUnits';
import { cn } from '@/lib/utils';

export interface AllocationValue {
  business_unit: BusinessUnit;
  percentage: number;
}

interface Props {
  value: AllocationValue[];
  onChange: (next: AllocationValue[]) => void;
  compact?: boolean;
}

export const BUAllocationEditor: React.FC<Props> = ({ value, onChange, compact }) => {
  // Build complete row set (one per BU)
  const rows = useMemo(() => {
    const map = new Map(value.map((v) => [v.business_unit, Number(v.percentage) || 0]));
    return BUSINESS_UNITS.map((b) => ({ bu: b, pct: map.get(b.value) ?? 0 }));
  }, [value]);

  const total = rows.reduce((s, r) => s + (Number(r.pct) || 0), 0);
  const totalRounded = Math.round(total * 100) / 100;
  const isValid = totalRounded === 100 || totalRounded === 0;

  const setPct = (bu: BusinessUnit, pct: number) => {
    const next: AllocationValue[] = rows
      .map((r) => ({ business_unit: r.bu.value, percentage: r.bu.value === bu ? pct : r.pct }))
      .filter((r) => r.percentage > 0);
    onChange(next);
  };

  const setSingle = (bu: BusinessUnit) => {
    onChange([{ business_unit: bu, percentage: 100 }]);
  };

  const clearAll = () => onChange([]);

  const distributeEvenly = () => {
    const active = rows.filter((r) => r.pct > 0);
    const target = active.length > 0 ? active : rows;
    const each = Math.floor((100 / target.length) * 100) / 100;
    const result: AllocationValue[] = target.map((r, i) => ({
      business_unit: r.bu.value,
      percentage: i === target.length - 1 ? Math.round((100 - each * (target.length - 1)) * 100) / 100 : each,
    }));
    onChange(result);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className={compact ? 'text-xs' : ''}>Business Unit Allocation</Label>
        <div className="flex items-center gap-2">
          <Badge variant={isValid ? 'default' : 'destructive'}>
            Total: {totalRounded}%{totalRounded === 0 ? ' (unassigned)' : ''}
          </Badge>
        </div>
      </div>

      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.bu.value} className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
              style={{ background: r.bu.color }}
            />
            <span className={cn('flex-1 text-sm', compact && 'text-xs')}>{r.bu.label}</span>
            <Input
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={r.pct === 0 ? '' : r.pct}
              onChange={(e) => setPct(r.bu.value, Number(e.target.value) || 0)}
              className="w-24 h-8 text-right"
              placeholder="0"
            />
            <span className="text-xs text-muted-foreground w-4">%</span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={() => setSingle(r.bu.value)}
              title="Set 100%"
            >
              100
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-1">
        <Button type="button" size="sm" variant="outline" onClick={distributeEvenly}>
          Distribute evenly
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={clearAll}>
          Clear
        </Button>
      </div>
      {!isValid && (
        <p className="text-xs text-destructive">Allocations must sum to 100% (or be empty).</p>
      )}
    </div>
  );
};

export const allocationsAreValid = (rows: AllocationValue[]) => {
  const total = rows.reduce((s, r) => s + (Number(r.percentage) || 0), 0);
  const t = Math.round(total * 100) / 100;
  return t === 0 || t === 100;
};
